import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Group,
  Stack,
  Paper,
} from '@mantine/core';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import {
  defaultFilter,
  filterSchema,
  type Filter,
} from '@/schemas/API/POST/statistics/request';
import { createFormContext, zodResolver } from '@mantine/form';
import { MonthPickerInput, YearPickerInput } from '@mantine/dates';
import { formatDateToYYYYMMDD } from '@/utils/Date';
import { ApplicantStatisticsComponent } from '@/components/Statistics/ApplicantStatistics';
import { EmployeeStatisticsComponent } from '@/components/Statistics/EmployeeStatistics';
import { ClientStatisticsComponent } from '@/components/Statistics/ClientStatistics';
import { isEqual } from 'lodash';
import FilterModal, { type Locality } from '@/components/Statistics/StatisticsFilterModal/FilterModal';
import { getFetcher } from '@/api/fetchers';


export const [FilterFormProvider, useFilterFormContext, useFilterForm] =
  createFormContext<Filter>();

const CATEGORY_OPTIONS = [
  { label: 'Zaměstnanci', value: 'employees' },
  { label: 'Klienti', value: 'clients' },
  { label: 'Zájemci', value: 'applicants' },
] as const;

const VIEW_MODES = [
  { label: 'Měsíc', value: 'month' },
  { label: 'Rok', value: 'year' },
] as const;

type Category = (typeof CATEGORY_OPTIONS)[number]['value'];
type ViewMode = (typeof VIEW_MODES)[number]['value'];


const DateSelector = ({
  viewMode,
  selectedDate,
  setSelectedDate,
}: {
  viewMode: ViewMode;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
}) =>
  viewMode === 'month' ? (
    <MonthPickerInput
      data-testid='month-picker'
      value={selectedDate}
      onChange={setSelectedDate}
      placeholder="Vyberte měsíc"
      size="md"
      locale="cs"
    />
  ) : (
    <YearPickerInput
      data-testid='year-picker'
      value={selectedDate}
      onChange={setSelectedDate}
      size="md"
      locale="cs"
    />
  );

export const StatisticsPage = (): JSX.Element => {
  const [selectedCategory, setSelectedCategory] =
    useState<Category>('employees');
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [localities, setLocalities] = useState<Locality[]>([]);

  useEffect(() => {
    const fetchAllLocalities = async () => {
      const allLocalities: Locality[] = [];
      let page = 0;
      let done = false;
  
      while (!done) {
        const response = await getFetcher(`/localities?page=${page}`);
        const items: Locality[] = response?.content ?? [];
        allLocalities.push(...items);
        done = items.length < 20;
        page++;
      }
  
      setLocalities(allLocalities);
    };
  
    if (localities.length === 0) {
      fetchAllLocalities();
    }
  }, []);

  const [submittedFilter, setSubmittedFilter] = useState<Filter | null>(
    defaultFilter
  );

  const submittedFilterRef = useRef<Filter | null>(defaultFilter);

  const form = useFilterForm({
    initialValues: defaultFilter,
    validate: zodResolver(filterSchema),
  });

  useEffect(() => {
    form.setFieldValue('workedHoursFrom', 0);
    form.setFieldValue('workedHoursTo', viewMode === 'month' ? 200 : 2250);

    const updatedFilter: Filter = {
      ...form.values,
      type: viewMode === 'month' ? 'MONTHLY' : 'YEARLY',
      date: selectedDate
        ? formatDateToYYYYMMDD(selectedDate)
        : formatDateToYYYYMMDD(new Date()),
    };

    if(!isEqual(updatedFilter, submittedFilterRef.current)){
      submittedFilterRef.current = updatedFilter;
      setSubmittedFilter(updatedFilter);
    }
  }, [viewMode, selectedCategory, selectedDate]);

  const handleFilterSubmit = async (values: Filter) => {
    const updatedValues: Filter = {
      ...values,
      type: viewMode === 'month' ? 'MONTHLY' : 'YEARLY',
      date: selectedDate
        ? formatDateToYYYYMMDD(selectedDate)
        : formatDateToYYYYMMDD(new Date()),
    };

    form.setValues(updatedValues);

    setIsFilterOpen(false);

    setSubmittedFilter(updatedValues); // submitted filter is being sent
  };

  return (
    <>
      <FilterFormProvider form={form}>
        <Stack className="p-8">
          <PageHeader name="Statistika" />
          <Paper withBorder radius="md" className="p-4">
            <Group align="center">
              <Group style={{ flex: 2, justifyContent: 'flex-start' }}>
                {CATEGORY_OPTIONS.map(({ label, value }) => (
                  <Button
                    key={value}
                    variant={selectedCategory === value ? 'light' : 'subtle'}
                    onClick={() => setSelectedCategory(value)}
                    size="md"
                  >
                    {label}
                  </Button>
                ))}
              </Group>
              <Button.Group style={{ flex: 0.5, justifyContent: 'center' }}>
                {VIEW_MODES.map(({ label, value }) => (
                  <Button
                    key={value}
                    variant={viewMode === value ? 'light' : 'subtle'}
                    onClick={() => setViewMode(value)}
                  >
                    {label}
                  </Button>
                ))}
              </Button.Group>

              <DateSelector
                viewMode={viewMode}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />

              <Button
                variant="outline"
                size="md"
                onClick={() => setIsFilterOpen(true)}
              >
                Filtr
              </Button>
            </Group>
          </Paper>
        </Stack>

        <FilterModal
          opened={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          selectedCategory={selectedCategory}
          onSubmit={handleFilterSubmit}
          monthly={viewMode === 'month'}
          date={selectedDate}
          localities={localities}
        />
      </FilterFormProvider>
      <Stack className="px-8">
        {selectedCategory === 'employees' && submittedFilter && (
          <EmployeeStatisticsComponent filter={submittedFilter} />
        )}
        {selectedCategory === 'applicants' && submittedFilter && (
          <ApplicantStatisticsComponent filter={submittedFilter} />
        )}
        {selectedCategory === 'clients' && submittedFilter && (
          <ClientStatisticsComponent filter={submittedFilter} />
        )}
      </Stack>
    </>
  );
};
